import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material";
import { useTradingColors } from "../../Spot/tradingColorConfig";

const INTERVALS = [
  { label: "1m", value: "1" },
  { label: "3m", value: "3" },
  { label: "5m", value: "5" },
  { label: "15m", value: "15" },
  { label: "30m", value: "30" },
  { label: "1h", value: "60" },
  { label: "4h", value: "240" },
  { label: "1D", value: "1D" },
  { label: "1W", value: "1W" },
  { label: "1M", value: "1M" },
];

const normalizeOptionsAsset = (a) => {
  const cCode = (a?.currencyCode || a?.currencycode || "").toUpperCase();
  const bCode = (a?.baseCurrency || a?.basecurrency || "USDT").toUpperCase();
  const pair = a?.assetPair || `${cCode}${bCode}`;
  const name = a?.assetPairName || a?.assetPair || `${cCode} Options`;

  return {
    currencyCode: cCode,
    baseCurrency: bCode,
    assetPair: pair,
    assetPairName: name,
    ltpValue: a?.ltpValue != null ? a.ltpValue : a?.lastTrade1 != null ? a.lastTrade1 : "",
    roc: a?.roc != null ? parseFloat(a.roc) : 0,
    volume: a?.volume || 0,
    contractValue: a?.contractValue || cCode,
  };
};

const OptionChartSlotHeader = ({
  currencycode,
  basecurrency,
  assetpair,
  assetPairName,
  interval = "60",
  onIntervalSelect,
  onSymbolSelect,
  isActive = false,
  btnColor: propBtnColor,
  onActivate,
  onClose,
  showClose = false,
}) => {
  const reduxBtnColor = useSelector((state) => state?.btncolor);
  const themeBtnColor = propBtnColor || reduxBtnColor || "#1976d2";
  const { upColor, downColor } = useTradingColors();

  const [symbolMenuOpen, setSymbolMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("ALL");
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  // Load available contracts from localStorage
  const allContracts = useMemo(() => {
    try {
      const raw = localStorage.getItem("options_assets");
      if (raw) {
        const parsed = JSON.parse(raw);
        const list = Array.isArray(parsed) ? parsed : parsed?.Values;
        if (Array.isArray(list) && list.length > 0) {
          return list.map(normalizeOptionsAsset);
        }
      }
    } catch (e) {
      console.warn("Failed to load options_assets for slot header:", e);
    }
    return [];
  }, [symbolMenuOpen]);

  // Filter contracts
  const filteredContracts = useMemo(() => {
    let list = allContracts;
    if (filterType !== "ALL") {
      list = list.filter((c) => c.contractValue === filterType || c.currencyCode === filterType);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (c) =>
          c.assetPair.toLowerCase().includes(q) ||
          c.assetPairName.toLowerCase().includes(q) ||
          c.currencyCode.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allContracts, filterType, searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        symbolMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setSymbolMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [symbolMenuOpen]);

  const displayName = assetPairName || assetpair || `${currencycode || "BTC"}/${basecurrency || "USDT"}`;

  return (
    <div
      onClick={onActivate}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "32px",
        minHeight: "32px",
        padding: "0 8px",
        backgroundColor: "var(--body-color-less, #181a20)",
        borderBottom: "1px solid rgba(125,125,125,0.14)",
        boxSizing: "border-box",
        position: "relative",
        userSelect: "none",
        borderTop: isActive ? `2px solid ${themeBtnColor}` : "2px solid transparent",
      }}
    >
      {/* Left side: Symbol Selector + Active indicator */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {isActive && (
          <span
            style={{
              fontSize: "9px",
              fontWeight: 800,
              textTransform: "uppercase",
              padding: "1px 5px",
              borderRadius: "3px",
              backgroundColor: alpha(themeBtnColor, 0.2),
              color: themeBtnColor,
              border: `1px solid ${alpha(themeBtnColor, 0.4)}`,
              letterSpacing: "0.04em",
            }}
          >
            Active
          </span>
        )}

        {/* Symbol Button */}
        <div style={{ position: "relative" }}>
          <button
            ref={btnRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSymbolMenuOpen((prev) => !prev);
              if (onActivate) onActivate();
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "2px 6px",
              fontSize: "11px",
              fontWeight: 700,
              color: "var(--text-color, #fff)",
              backgroundColor: symbolMenuOpen ? alpha(themeBtnColor, 0.15) : "transparent",
              border: `1px solid ${symbolMenuOpen ? alpha(themeBtnColor, 0.4) : "rgba(125,125,125,0.18)"}`,
              borderRadius: "4px",
              cursor: "pointer",
              outline: "none",
              transition: "all 0.12s ease",
            }}
          >
            <span style={{ maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {displayName}
            </span>
            <svg
              width="8"
              height="5"
              viewBox="0 0 8 5"
              fill="none"
              style={{
                transform: symbolMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.15s ease",
                opacity: 0.6,
              }}
            >
              <path d="M1 1L4 4L7 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Symbol Dropdown */}
          {symbolMenuOpen && (
            <div
              ref={menuRef}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 99999,
                width: "280px",
                maxHeight: "340px",
                backgroundColor: "var(--body-color-less, #181a20)",
                border: "1px solid rgba(125,125,125,0.22)",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Search input */}
              <div style={{ padding: "8px", borderBottom: "1px solid rgba(125,125,125,0.12)" }}>
                <input
                  type="text"
                  placeholder="Search contract..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "4px 8px",
                    fontSize: "11px",
                    color: "var(--text-color, #fff)",
                    backgroundColor: "rgba(125,125,125,0.08)",
                    border: "1px solid rgba(125,125,125,0.2)",
                    borderRadius: "4px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Contract List */}
              <div style={{ flex: 1, overflowY: "auto", maxHeight: "250px" }}>
                {filteredContracts.length === 0 ? (
                  <div style={{ padding: "16px", textAlign: "center", fontSize: "11px", opacity: 0.5, color: "var(--text-color)" }}>
                    No contracts found
                  </div>
                ) : (
                  filteredContracts.map((c) => {
                    const isCurrent = c.assetPair === assetpair;
                    const isPositive = (c.roc || 0) >= 0;
                    return (
                      <div
                        key={c.assetPair}
                        onClick={() => {
                          if (onSymbolSelect) onSymbolSelect(c);
                          setSymbolMenuOpen(false);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "6px 10px",
                          fontSize: "11px",
                          cursor: "pointer",
                          backgroundColor: isCurrent ? alpha(themeBtnColor, 0.15) : "transparent",
                          color: "var(--text-color, #fff)",
                          transition: "background 0.12s ease",
                          borderBottom: "1px solid rgba(125,125,125,0.06)",
                        }}
                        onMouseEnter={(e) => {
                          if (!isCurrent) e.currentTarget.style.backgroundColor = "rgba(125,125,125,0.08)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isCurrent) e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "11px" }}>{c.assetPairName || c.assetPair}</div>
                          <div style={{ fontSize: "9px", opacity: 0.5 }}>{c.assetPair}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 600, fontSize: "11px" }}>{c.ltpValue || "-"}</div>
                          <div style={{ fontSize: "9px", color: isPositive ? upColor : downColor, fontWeight: 700 }}>
                            {isPositive ? "+" : ""}
                            {c.roc}%
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right side: Intervals & Close */}
      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
        {INTERVALS.map((item) => {
          const isSelected = String(interval) === String(item.value);
          return (
            <button
              key={item.value}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onIntervalSelect) onIntervalSelect(item.value);
                if (onActivate) onActivate();
              }}
              style={{
                background: isSelected ? alpha(themeBtnColor, 0.2) : "transparent",
                border: "none",
                borderRadius: "3px",
                color: isSelected ? themeBtnColor : "var(--text-color, #aaa)",
                fontSize: "10px",
                fontWeight: isSelected ? 700 : 500,
                padding: "2px 5px",
                cursor: "pointer",
                outline: "none",
                transition: "all 0.1s ease",
              }}
            >
              {item.label}
            </button>
          );
        })}

        {showClose && onClose && (
          <button
            type="button"
            title="Close this slot"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-color, #888)",
              cursor: "pointer",
              padding: "2px 4px",
              marginLeft: "4px",
              borderRadius: "3px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default OptionChartSlotHeader;
